import { useCallback, useEffect, useRef, useState } from 'react';
import { useSessionStore } from '../store/sessionStore';
import { HomeBackdrop } from './home/HomeBackdrop';
import { spendInviteCode, takeInviteCode } from './home/inviteCode';
import { JoinView } from './home/JoinView';
import { HomeStatus } from './home/HomeStatus';
import { MainMenu } from './home/MainMenu';
import { PlayView, type HomeAction } from './home/PlayView';
import { SubView } from './home/SubView';
import { OptionsModal } from './options/OptionsModal';
import { useEscapeLayer } from './useEscapeLayer';

export { BOTS_UNAVAILABLE_HINT } from './home/PlayView';

type HomeView = 'menu' | 'play' | 'join';

/**
 * Home: a centred menu (name, Play, Join Lobby, Options) over the backdrop. Play and Join Lobby
 * swap the menu for their sub-view; Options is a modal over it. An invite link opens Join Lobby.
 * Local state, so returning from a match starts on the menu again.
 */
export function HomeScreen() {
  const [invite] = useState(takeInviteCode);
  const [view, setView] = useState<HomeView>(() => (invite ? 'join' : 'menu'));
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [pending, setPending] = useState<HomeAction | null>(null);
  const [isPrivate, setPrivate] = useState(false);
  const busy = useSessionStore((s) => s.status === 'connecting');
  const optionsButton = useRef<HTMLButtonElement>(null);
  const reopenFocus = useRef(false);

  // Leaving Home means a room was joined: the invite is used up.
  useEffect(() => spendInviteCode, []);

  // An error belongs to the view it happened in.
  const go = (next: HomeView) => {
    useSessionStore.setState({ error: null });
    setView(next);
  };
  // A connect in progress swaps to the match anyway; Back would only hide its status.
  const back = () => !busy && go('menu');
  useEscapeLayer(back, view !== 'menu');

  const closeOptions = useCallback(() => {
    reopenFocus.current = true;
    setOptionsOpen(false);
  }, []);
  useEffect(() => {
    if (optionsOpen || !reopenFocus.current) return;
    reopenFocus.current = false;
    optionsButton.current?.focus();
  }, [optionsOpen]);

  return (
    <main className="home">
      <HomeBackdrop />
      <div className="home-layout">
        {view === 'menu' && (
          <MainMenu
            pending={pending}
            onPlay={() => go('play')}
            onJoin={() => go('join')}
            onOptions={() => setOptionsOpen(true)}
            optionsRef={optionsButton}
          />
        )}
        {view === 'play' && (
          <SubView title="Play" className="play-view" onBack={back} backDisabled={busy}>
            <PlayView
              pending={pending}
              onAction={setPending}
              isPrivate={isPrivate}
              setPrivate={setPrivate}
            />
            <HomeStatus pending={pending} />
          </SubView>
        )}
        {view === 'join' && (
          <SubView title="Join Lobby" className="join-view" onBack={back} backDisabled={busy}>
            <JoinView invite={invite} pending={pending} onAction={setPending} />
            <HomeStatus pending={pending} />
          </SubView>
        )}
      </div>
      {optionsOpen && <OptionsModal onClose={closeOptions} />}
    </main>
  );
}
